import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PRIORITY_COLOR, PRIORITY_LABEL } from "./PriorityBadge";

/**
 * Month grid — 4 usability upgrades over the previous version:
 *
 *   1. Algerian week-end (Fri + Sat) columns get a slightly darker tint so
 *      the eye jumps to work days first.
 *   2. If a day has more than 3 tasks, the extras collapse into a
 *      "+N autres" button that opens a modal listing every task of the day.
 *   3. Each pill now shows the anchor **time** before the title:
 *      "09:00 · Envoyer contrat client Djamel".
 *   4. Clicking an *empty* day cell fires `onCreateOnDate(iso)` so the
 *      parent page can open the New Task dialog with `execution_date`
 *      pre-filled to that date (defaults to 09:00 local time).
 *
 * The anchor for a task in the calendar is `execution_date`, falling back
 * to `deadline` when no execution date is set.
 */

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
// Column indices (0 = Monday). Algerian weekend = Fri (4) + Sat (5).
const WEEKEND_COLS = new Set([4, 5]);

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Build the 6-week grid (42 cells) — Monday-first. */
function buildGrid(cursor) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const shift = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - shift);
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

/** Reusable pill representation of one task in the calendar. */
function TaskPill({ task, onClick, dense = false }) {
  const c = PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.normal;
  const anchor = task.execution_date || task.deadline;
  const timeStr = fmtTime(anchor);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="w-full text-left px-1.5 py-0.5 rounded text-[11px] leading-tight truncate hover:opacity-90 transition-opacity"
      style={{
        background: `${c}26`,
        border: `1px solid ${c}`,
        color: "#EDEDED",
      }}
      title={`${task.subject} · ${PRIORITY_LABEL[task.priority]}`}
      data-testid={dense ? `cal-modal-task-${task.id}` : `cal-task-${task.id}`}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
        style={{ background: c }}
        aria-hidden="true"
      />
      {timeStr && (
        <span className="font-semibold tabular-nums mr-1">{timeStr}</span>
      )}
      <span className="opacity-70 mr-1">·</span>
      {task.subject}
    </button>
  );
}

export default function TaskCalendarView({ items, onCreateOnDate, initialMonth, onMonthChange }) {
  const navigate = useNavigate();
  // Init from URL (`YYYY-MM`) so the parent can restore the previously
  // browsed month after a task-detail round-trip.
  const [cursor, setCursor] = useState(() => {
    if (initialMonth && /^\d{4}-\d{2}$/.test(initialMonth)) {
      const [y, m] = initialMonth.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [dayModal, setDayModal] = useState(null); // { date: Date, tasks: [] }
  const today = new Date();

  // Notify the parent whenever the cursor month changes so the URL query
  // (`?month=YYYY-MM`) can stay in sync. We omit the query when the user
  // is on the current month to keep URLs clean.
  useEffect(() => {
    if (!onMonthChange) return;
    const ym = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    const now = new Date();
    const nowYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    onMonthChange(ym === nowYm ? null : ym);
  }, [cursor, onMonthChange]);

  const byDay = useMemo(() => {
    const m = new Map();
    for (const t of items) {
      const iso = t.execution_date || t.deadline;
      if (!iso) continue;
      const d = new Date(iso);
      const k = dayKey(d);
      const arr = m.get(k) || [];
      arr.push(t);
      m.set(k, arr);
    }
    // Sort inside each day by the anchor time so the pills read as a schedule.
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ta = new Date(a.execution_date || a.deadline).getTime();
        const tb = new Date(b.execution_date || b.deadline).getTime();
        return ta - tb;
      });
    }
    return m;
  }, [items]);

  const grid = useMemo(() => buildGrid(cursor), [cursor]);

  const goto = (delta) => {
    const d = new Date(cursor);
    d.setMonth(cursor.getMonth() + delta);
    setCursor(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setDate(1);
    setCursor(d);
  };
  const monthLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  /** Format a Date object into "YYYY-MM-DDTHH:mm" for the datetime-local
   *  input on the create dialog. We default to 09:00 local time. */
  const dateToLocalInput = (d) => {
    const y = d.getFullYear();
    const mo = pad(d.getMonth() + 1);
    const da = pad(d.getDate());
    return `${y}-${mo}-${da}T09:00`;
  };

  const onEmptyCellClick = (d) => {
    if (!onCreateOnDate) return;
    onCreateOnDate(dateToLocalInput(d));
  };

  return (
    <div className="space-y-3" data-testid="task-calendar-view">
      {/* Toolbar ------------------------------------------------------------ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => goto(-1)} data-testid="cal-prev">
            <ChevronLeft size={14} />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">
            Aujourd&apos;hui
          </Button>
          <Button variant="outline" size="sm" onClick={() => goto(1)} data-testid="cal-next">
            <ChevronRight size={14} />
          </Button>
        </div>
        <div className="text-lg font-semibold capitalize" data-testid="cal-month-label">
          {monthLabel}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {["immediat", "urgent", "moyen", "normal"].map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ background: PRIORITY_COLOR[p], border: `1px solid ${PRIORITY_COLOR[p]}` }}
              />
              {PRIORITY_LABEL[p]}
            </span>
          ))}
        </div>
      </div>

      {/* Grid --------------------------------------------------------------- */}
      <div className="rounded-md border border-vertec-borderDark overflow-hidden">
        {/* Weekday header — Fri/Sat share the darker tint */}
        <div className="grid grid-cols-7 bg-white/5 text-xs uppercase tracking-wider text-muted-foreground">
          {WEEKDAYS.map((w, i) => (
            <div
              key={w}
              className="px-2 py-2 border-r border-vertec-borderDark last:border-r-0"
              style={WEEKEND_COLS.has(i) ? { background: "rgba(255,255,255,0.03)" } : undefined}
            >
              {w}
            </div>
          ))}
        </div>
        {/* 6 weeks × 7 days */}
        <div className="grid grid-cols-7 auto-rows-fr">
          {grid.map((d, idx) => {
            const col = idx % 7;
            const isWeekend = WEEKEND_COLS.has(col);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = isSameDay(d, today);
            const dayTasks = byDay.get(dayKey(d)) || [];
            const border = col === 6 ? "" : "border-r";
            const bottomBorder = idx < 35 ? "border-b" : "";

            // Layered background so weekend + out-of-month + hover all read.
            let cellBg = "transparent";
            if (!inMonth) cellBg = "rgba(255,255,255,0.02)";
            else if (isWeekend) cellBg = "rgba(255,255,255,0.035)"; // slightly darker

            const shown = dayTasks.slice(0, 3);
            const extra = dayTasks.length - shown.length;
            const isEmpty = dayTasks.length === 0;

            return (
              <div
                key={idx}
                className={`min-h-[110px] p-1.5 ${border} ${bottomBorder} border-vertec-borderDark relative group ${
                  inMonth ? "" : "text-muted-foreground"
                } ${isEmpty ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
                style={{ background: cellBg }}
                onClick={isEmpty ? () => onEmptyCellClick(d) : undefined}
                data-testid={`cal-cell-${dayKey(d)}`}
                data-empty={isEmpty ? "true" : "false"}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-[11px] font-medium tabular-nums ${
                      isToday
                        ? "inline-flex items-center justify-center rounded-full bg-vertec-blue text-white w-5 h-5"
                        : ""
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  {/* Ghost + button on hover of an empty cell, hints creation */}
                  {isEmpty && inMonth && (
                    <span
                      className="opacity-0 group-hover:opacity-70 text-muted-foreground transition-opacity"
                      aria-hidden="true"
                    ><Plus size={11} /></span>
                  )}
                </div>
                <div className="space-y-1">
                  {shown.map((t) => (
                    <TaskPill
                      key={t.id}
                      task={t}
                      onClick={() => navigate(`/taches/${t.id}`)}
                    />
                  ))}
                  {extra > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDayModal({ date: new Date(d), tasks: dayTasks });
                      }}
                      className="w-full text-[10px] text-center text-muted-foreground hover:text-foreground rounded py-0.5 transition-colors hover:bg-white/5"
                      data-testid={`cal-more-${dayKey(d)}`}
                    >
                      +{extra} autre{extra > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* -------- "Voir tout" modal for days that overflow ---------------- */}
      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="bg-card border-[#3A3A3A] max-w-md" data-testid="cal-day-modal">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {dayModal?.date.toLocaleDateString("fr-FR", {
                weekday: "long", day: "2-digit", month: "short", year: "numeric",
              })}
              {" · "}
              <span className="text-muted-foreground text-sm font-normal">
                {dayModal?.tasks.length} tâche{(dayModal?.tasks.length || 0) > 1 ? "s" : ""}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {(dayModal?.tasks || []).map((t) => (
              <TaskPill
                key={t.id}
                task={t}
                dense
                onClick={() => { setDayModal(null); navigate(`/taches/${t.id}`); }}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
